import { $t } from '@previo/core'
import { Codebook } from '../Codebook'
import { CountryId } from '../country/CountryId'
import { LockVendorId } from '../../enums'
import acsline from '../../assets/images/lock-systems/acsline.png'
import assaabloy from '../../assets/images/lock-systems/assaabloy.png'
import dormakaba from '../../assets/images/lock-systems/dormakaba.png'
import erozeta from '../../assets/images/lock-systems/erozeta.png'
import hbgroup from '../../assets/images/lock-systems/hbgroup.png'
import keyguru from '../../assets/images/lock-systems/keyguru.png'
import loxone from '../../assets/images/lock-systems/loxone.png'
import nuki from '../../assets/images/lock-systems/nuki.png'
import onity from '../../assets/images/lock-systems/onity.png'
import saltoKs from '../../assets/images/lock-systems/saltoks.png'
import saltoSpace from '../../assets/images/lock-systems/saltospace.png'
import smartkey from '../../assets/images/lock-systems/smartkey.png'
import tuya from '../../assets/images/lock-systems/tuya.png'
import { LockConnectionParamType } from './LockConnectionParamType'
import { LockKeyDeviceTypeId } from './LockKeyDeviceType'

/**
 * The vendorName and brand values should correspond to backend LockVendorId getName & getBrand
 */
export class LockVendorType extends Codebook<LockVendorId> {
  static readonly SALTO_SPACE = new LockVendorType(
    LockVendorId.SALTO_SPACE,
    'Salto Space',
    'Salto Space',
    null,
    true,
    () =>
      $t(
        'A smart locking system that allows unlocking with a card or in a modern way using a mobile phone.',
      ),
    saltoSpace,
    [
      LockConnectionParamType.HOST,
      LockConnectionParamType.PORT,
      LockConnectionParamType.RETENTION_MODE,
    ],
    true,
    [LockKeyDeviceTypeId.CARD, LockKeyDeviceTypeId.ALFRED_KEY],
    true,
    false,
    false,
    false,
  )

  static readonly ONITY = new LockVendorType(
    LockVendorId.ONITY,
    'Onity',
    'Onity',
    null,
    true,
    () =>
      $t(
        'Onity offers access card systems for complex access solutions in hotels and guesthouses.',
      ),
    onity,
    [
      LockConnectionParamType.HOST,
      LockConnectionParamType.PORT,
      LockConnectionParamType.RETENTION_MODE,
    ],
    true,
    [LockKeyDeviceTypeId.CARD],
    true,
    false,
    false,
    false,
  )

  static readonly ASSA_ABLOY = new LockVendorType(
    LockVendorId.ASSA_ABLOY,
    'Assa Abloy',
    'Assa Abloy',
    null,
    true,
    () =>
      $t(
        "Visionline platform from Assa Abloy represents what it means to ensure a safe, yet seamless hotel environment by equipping properties with the industry's highest security standards. Locks are opened with RFID cards.",
      ),
    assaabloy,
    [LockConnectionParamType.HOST, LockConnectionParamType.PORT],
    true,
    [LockKeyDeviceTypeId.CARD],
    true,
    false,
    false,
    false,
  )

  static readonly ACS_LINE_CARD = new LockVendorType(
    LockVendorId.ACS_LINE_CARD,
    'ACS Line',
    'ACS Line',
    null,
    true,
    () =>
      $t(
        "ACS-line is a card lock system used to secure all activities related to a guest's stay and the operation of a hotel or guesthouse. Each guest receives an electronic hotel card upon arrival instead of a classic key.",
      ),
    acsline,
    [
      LockConnectionParamType.HOST,
      LockConnectionParamType.PORT,
      LockConnectionParamType.RETENTION_MODE,
    ],
    true,
    [LockKeyDeviceTypeId.CARD],
    true,
    false,
    false,
    false,
  )

  static readonly TTLOCK_OFFLINE = new LockVendorType(
    LockVendorId.TTLOCK_OFFLINE,
    'TTLock Offline',
    'TTLock',
    null,
    false,
    () => '',
    null,
    [
      LockConnectionParamType.USERNAME,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.PASSWORD_HASH,
      LockConnectionParamType.CLIENT_ID,
      LockConnectionParamType.CLIENT_SECRET,
    ],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    true,
    true,
    false,
  )

  static readonly ACS_LINE_PIN = new LockVendorType(
    LockVendorId.ACS_LINE_PIN,
    'ACS Line PIN',
    'ACS Line',
    null,
    false, // same description and icon as ACS_LINE, don't feature them both
    () =>
      $t(
        "ACS-line is a card lock system used to secure all activities related to a guest's stay and the operation of a hotel or guesthouse. Each guest receives an electronic hotel card upon arrival instead of a classic key.",
      ),
    acsline,
    [LockConnectionParamType.HOST, LockConnectionParamType.PORT],
    true,
    [LockKeyDeviceTypeId.PIN],
    true,
    false,
    false,
    false,
  )

  static readonly TTLOCK_ONLINE = new LockVendorType(
    LockVendorId.TTLOCK_ONLINE,
    'TTLock Online',
    'e-rozeta',
    [CountryId.CZE, CountryId.SVK, CountryId.POL],
    true,
    () =>
      $t(
        'A multifunctional locking system that allows guests to unlock with PIN codes or cards. It saves hoteliers costs in conjunction with an unattended reception system.',
      ),
    erozeta,
    [
      LockConnectionParamType.USERNAME,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.PASSWORD_HASH,
      LockConnectionParamType.CLIENT_ID,
      LockConnectionParamType.CLIENT_SECRET,
    ],
    false,
    [LockKeyDeviceTypeId.PIN, LockKeyDeviceTypeId.CARD],
    false,
    true,
    true,
    false,
  )

  static readonly MANUAL_PIN = new LockVendorType(
    LockVendorId.MANUAL_PIN,
    'Manual PIN',
    'Manual PIN',
    null,
    false,
    () => '',
    null,
    [],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    false,
    false,
    false,
  )

  static readonly DORMAKABA = new LockVendorType(
    LockVendorId.DORMAKABA,
    'Dormakaba',
    'Dormakaba',
    null,
    true,
    () =>
      $t(
        "The Ambiance platform is Dormakaba's next-generation access management software. It is a scalable access control solution designed to work with electronic hotel locks.",
      ),
    dormakaba,
    [
      LockConnectionParamType.HOST,
      LockConnectionParamType.PORT,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.EXPANDED_KEY_NAME,
    ],
    true,
    [LockKeyDeviceTypeId.CARD],
    true,
    false,
    false,
    false,
  )

  static readonly KEYGURU = new LockVendorType(
    LockVendorId.KEYGURU,
    'Keyguru',
    'Keyguru',
    [CountryId.CZE, CountryId.SVK],
    true,
    () =>
      $t(
        'Self-service key boxes. A set of physical keys or a card can be inserted into the box. Guests collect the keys using a PIN code.',
      ),
    keyguru,
    [
      LockConnectionParamType.DEVICE_ID,
      LockConnectionParamType.API_KEY,
      LockConnectionParamType.KEYGURU_PLATFORM,
    ],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    true,
    true,
    false,
  )

  static readonly SMARTKEY = new LockVendorType(
    LockVendorId.SMARTKEY,
    'SmartKey',
    'SmartKey',
    [CountryId.CZE],
    true,
    () =>
      $t(
        'A Czech company specializing in lock, security and mechatronic technologies. Guests can unlock Smartkey locks with a PIN code, card or directly from the Alfred App.',
      ),
    smartkey,
    [
      LockConnectionParamType.USERNAME,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.PASSWORD_HASH,
      LockConnectionParamType.CLIENT_ID,
      LockConnectionParamType.CLIENT_SECRET,
      LockConnectionParamType.API_KEY,
    ],
    false,
    [LockKeyDeviceTypeId.PIN, LockKeyDeviceTypeId.CARD],
    false,
    true,
    true,
    false,
  )

  static readonly TUYA = new LockVendorType(
    LockVendorId.TUYA,
    'Tuya',
    'Tuya',
    null,
    true,
    () => $t('Smart door locks that open with a PIN code or remotely in the Alfred App.'),
    tuya,
    [LockConnectionParamType.CLIENT_ID, LockConnectionParamType.CLIENT_SECRET],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    true,
    true,
    false,
  )

  static readonly RANDOM_PIN = new LockVendorType(
    LockVendorId.RANDOM_PIN,
    'Random PIN',
    'Loxone',
    [CountryId.CZE],
    true,
    () =>
      $t(
        'Loxone offers intelligent automation for residential, industrial, hotel and office buildings',
      ),
    loxone,
    [LockConnectionParamType.PIN_LENGTH],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    false,
    false,
    false,
  )

  static readonly STAR_SMART = new LockVendorType(
    LockVendorId.STAR_SMART,
    'STAR Smart',
    'STAR Smart',
    [CountryId.CZE, CountryId.SVK, CountryId.POL],
    true,
    () =>
      $t(
        'The modern STAR Smart locking system from the Czech family-run company HB Group offers the possibility of convenient unlocking with a smartphone, code, chip or fingerprint.',
      ),
    hbgroup,
    [
      LockConnectionParamType.USERNAME,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.PASSWORD_HASH,
      LockConnectionParamType.CLIENT_ID,
      LockConnectionParamType.CLIENT_SECRET,
    ],
    false,
    [LockKeyDeviceTypeId.PIN, LockKeyDeviceTypeId.CARD],
    false,
    true,
    true,
    false,
  )

  static readonly SALTO_KS = new LockVendorType(
    LockVendorId.SALTO_KS,
    'Salto KS',
    'Salto KS',
    null,
    true,
    () => $t('The Salto KS smart access system allows keyless unlocking using PIN codes.'),
    saltoKs,
    [
      LockConnectionParamType.USERNAME,
      LockConnectionParamType.PASSWORD,
      LockConnectionParamType.CLIENT_ID,
      LockConnectionParamType.CLIENT_SECRET,
      LockConnectionParamType.SITE_ID,
      LockConnectionParamType.ROLE_ID,
    ],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    true,
    true,
    false,
  )

  static readonly NUKI = new LockVendorType(
    LockVendorId.NUKI,
    'Nuki',
    'Nuki',
    null,
    true,
    () =>
      $t(
        'With Nuki smart locks you can offer your guests keyless access. Save time and money and share PIN codes instead of physical keys. Fully automated and secure.',
      ),
    nuki,
    [],
    false,
    [LockKeyDeviceTypeId.PIN],
    false,
    true,
    true,
    true,
  )

  private constructor(
    id: LockVendorId,
    public readonly vendorName: string,
    public readonly brand: string,
    public readonly countries: CountryId[] | null,
    public readonly isFeatured: boolean,
    public readonly description: () => string,
    public readonly icon: string | null,
    public readonly requiredConnectionParams: LockConnectionParamType[],
    public readonly usesEncoders: boolean,
    public readonly supportedKeyDeviceTypes: LockKeyDeviceTypeId[],
    public readonly isTestable: boolean,
    public readonly usesExternalName: boolean,
    public readonly canDownloadLocks: boolean,
    public readonly usesOAuth: boolean,
  ) {
    super(id)
  }
}
